import { forwardRef, useMemo, useState } from 'react';

import { DownloadPopup } from '@src/components/DownloadPopup';
import {
  DexterV1AlphaInferenceDownloadPayload,
  DexterV1AlphaInsightResponse,
  inputInferences,
  outputInferences
} from '@src/containers/Viewer/subComponents/Insights/types/insights';
import { downloadAssetApis } from '@src/utils/helpers/assetHelpers';

export const DownloadInsightsPopup = forwardRef(
  ({ activeInsight }: { activeInsight: DexterV1AlphaInsightResponse }, ref) => {
    const { putDexterMultipleAssetsByInsightId } = downloadAssetApis();

    const [downloadPayload, setDownloadPayload] = useState<
      DexterV1AlphaInferenceDownloadPayload | undefined
    >({
      inputs: [],
      outputs: []
    });

    const inputs: inputInferences[] = useMemo(
      () => activeInsight?.input.filter(i => i?.is_artifact === true) ?? [],
      [activeInsight]
    );
    const outputs: outputInferences[] = useMemo(
      () => activeInsight?.output.filter(i => i?.is_artifact === true) ?? [],
      [activeInsight]
    );

    const downloadPopupSchema = useMemo(() => {
      const outputGroups = outputs.filter(i => i.group);
      const nonGroupedOutputs = outputs.filter(i => !i.group);
      const groups = [...new Set(outputGroups.map(i => i.group))];

      const schema = {
        Inputs: [
          ...inputs.map(i => ({
            ...i,
            id: i.name,
            title: i.display_name || i.name,
            description: i.description || ''
          }))
        ],
        Outputs: [
          ...groups.map(g => {
            const groupFormats = outputGroups
              .filter(o => o.group === g)
              .map(o => o.format)
              .filter(Boolean) as string[];
            return {
              id: g,
              title: g,
              description: g,
              formats: [...new Set(groupFormats)]
            };
          }),
          ...nonGroupedOutputs.map(o => ({
            id: o.name,
            title: o.display_name || o.name,
            description: o.description || '',
            formats: o.format ? [o.format] : []
          }))
        ]
      };

      return schema;
    }, [outputs, inputs]);

    const onCheckedChange = (id: string, group_name: string) => {
      setDownloadPayload(
        (prevState: DexterV1AlphaInferenceDownloadPayload | undefined) => {
          if (!prevState) {
            return prevState as
              | DexterV1AlphaInferenceDownloadPayload
              | undefined; // or throw error
          }

          if (group_name === 'Inputs') {
            if (prevState.inputs.includes(id)) {
              return {
                ...prevState,
                inputs: [...prevState.inputs.filter(item => item !== id)]
              };
            } else {
              return { ...prevState, inputs: [...prevState.inputs, id] };
            }
          }

          // for handling outputs
          if (group_name === 'Outputs') {
            if (prevState.outputs.includes(id)) {
              return {
                ...prevState,
                outputs: [...prevState.outputs.filter(item => item !== id)]
              };
            } else {
              return { ...prevState, outputs: [...prevState.outputs, id] };
            }
          }
        }
      );
    };

    const groupingDownloadPayload = useMemo(() => {
      if (!downloadPayload) return { inputs: [], outputs: [] };

      // Get unique group names from outputs
      const groupNames = [
        ...new Set(outputs.map(o => o.group).filter(Boolean))
      ];

      // Filter group names that are in the payload
      const groupsInPayload = groupNames.filter(group =>
        downloadPayload.outputs.includes(group!)
      );

      // Get output names that belong to those groups
      const groupedOutputNames = outputs
        .filter(o => groupsInPayload.includes(o.group!))
        .map(o => o.name);

      // Get non-group outputs
      const remainingOutputs = downloadPayload.outputs.filter(
        o => !groupsInPayload.includes(o)
      );

      return {
        inputs: downloadPayload.inputs,
        outputs: [...groupedOutputNames, ...remainingOutputs]
      };
    }, [downloadPayload, outputs]);

    return (
      <DownloadPopup
        ref={ref}
        // NOTE: API does not depend on fileName, provides their own filename for zipped file
        // But filename can be used later in other parts of the codebase
        filename=''
        downloadGroupSchema={downloadPopupSchema}
        startDownloadFn={() =>
          putDexterMultipleAssetsByInsightId(
            activeInsight.id,
            groupingDownloadPayload
          )
        }
        fetchStatusFn={() =>
          putDexterMultipleAssetsByInsightId(
            activeInsight.id,
            groupingDownloadPayload
          )
        }
        onCheckedChange={onCheckedChange}
      />
    );
  }
);

DownloadInsightsPopup.displayName = 'DownloadInsightsPopup';
